import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { MEDIA } from "@/lib/media";
import { WaveDivider, Paw, Bone, PawField } from "@/components/decor";

const FIELDS = ["Nickname", "Birthday", "Favourite Food", "Favourite Toy", "Favourite Place", "Best Quality", "Naughtiest Habit", "Special Talent"];

const CHEWIE = {
  name: "CHEWIE", img: MEDIA.chewie, accent: "#FF6B9D",
  data: { "Nickname": "Magoo", "Birthday": "24th June 2022", "Favourite Food": "Biscuits (obviously)",
    "Favourite Toy": "Anything that is Solo's", "Favourite Place": "The best spot on the couch", "Best Quality": "Endless cuddles",
    "Naughtiest Habit": "Giving the side eye", "Special Talent": "Can look directly at you and still completely ignore everything you say to her" },
};
const SOLO = {
  name: "SOLO", img: MEDIA.solo, accent: "#4CC9F0",
  data: { "Nickname": "Monster", "Birthday": "1st April 2024", "Favourite Food": "Also biscuits",
    "Favourite Toy": "Any old sock", "Favourite Place": "Wherever Chewie isn't looking", "Best Quality": "Gives the best snuggle cuddles",
    "Naughtiest Habit": "Loves to chew on wood — particularly his mum's outdoor garden box / storage bench", "Special Talent": "Looking innocent" },
};

function ProfileCard({ dog }) {
  return (
    <div className="bg-white rounded-[2rem] soft-shadow overflow-hidden card-hover" data-testid={`profile-${dog.name.toLowerCase()}`}>
      <div className="relative aspect-square p-2 flex items-center justify-center" style={{ background: `${dog.accent}22` }}>
        <img src={dog.img} alt={`${dog.name}, a real British Bulldog`} className="w-full h-full object-contain rounded-2xl" />
      </div>
      <div className="p-6">
        <h3 className="font-display font-extrabold text-3xl text-[#19324A]" style={{ color: dog.accent }}>{dog.name}</h3>
        <dl className="mt-4 divide-y divide-[#19324A]/8">
          {FIELDS.map((f) => (
            <div key={f} className="flex justify-between gap-4 py-2.5">
              <dt className="font-display font-semibold text-[#19324A]/60 text-sm">{f}</dt>
              <dd className="text-[#19324A] font-medium text-sm text-right">{dog.data[f]}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

export default function MeetChewieSolo() {
  useSeo("Meet Chewie & Solo — The Real British Bulldogs",
    "Meet Chewie & Solo, the two real British Bulldogs behind The Happy Bulldog Co. and the stars of the Chewie & Solo Adventure books.");
  return (
    <div>
      <section className="relative bg-[#FFD93D]/25 py-14 sm:py-20 overflow-hidden text-center">
        <PawField color="#FFD93D" opacity={0.12} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <span className="font-hand text-2xl text-[#FF6B9D]">The real stars of the show</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-6xl text-[#19324A] mt-1">MEET CHEWIE & SOLO</h1>
          <p className="mt-5 text-[#19324A]/80 text-lg">Every great adventure needs great characters. Ours just happen to have four paws, wrinkly faces and absolutely no respect for personal space.</p>
        </div>
        <WaveDivider color="#FFF9EE" />
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 grid md:grid-cols-2 gap-8">
        <ProfileCard dog={CHEWIE} />
        <ProfileCard dog={SOLO} />
      </section>

      <section className="relative bg-[#FF6B9D] text-white py-16 overflow-hidden">
        <PawField color="#ffffff" opacity={0.12} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Bone className="w-20 mx-auto text-[#FFD93D] mb-4" />
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl">TOGETHER…</h2>
          <p className="mt-5 text-white/95 text-lg">They're partners in crime, professional nappers and the inspiration behind every Chewie & Solo Adventure.</p>
          <Link to="/shop" className="btn-primary mt-7 inline-flex">Shop the Collection</Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#19324A] text-center mb-8">A Few Real Adventures 🐾</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {[MEDIA.heroDogs, MEDIA.chewie, MEDIA.solo, MEDIA.heroDogs].map((img, i) => (
            <div key={i} className="rounded-2xl overflow-hidden aspect-square soft-shadow"><img src={img} alt="Chewie and Solo" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" /></div>
          ))}
        </div>
        <p className="text-center text-sm text-[#19324A]/50 mt-4">Real photos of Chewie & Solo — more mischief coming soon.</p>
      </section>
    </div>
  );
}
